/**
 * Claims Analytics API client (PHI — HR-admin only).
 *
 * Upload a carrier file, list reports, fetch one with its AI analysis,
 * (re)run analysis, delete. The dashboard renders the aggregate
 * ``analysis_result`` — never individual records.
 */
import client from "./client";

export interface ClaimsReport {
  id: string;
  carrier_name: string | null;
  data_type: string;
  report_period_start: string | null;
  report_period_end: string | null;
  file_name: string | null;
  total_claims: number | null;
  total_spend: number | null;
  total_members: number | null;
  status: string; // processing | parsed | analyzing | analyzed | error
  error_message: string | null;
  created_at: string | null;
}

export interface ClaimsStats {
  total_claims: number;
  total_spend: number;
  total_members: number;
  months: number;
  pmpm: number;
  period: string | null;
  by_diagnosis: { category: string; claims: number; spend: number; pct: number }[];
  top_diagnoses: { code: string; category: string; spend: number }[];
  by_service_type: { provider_type: string; visits: number; spend: number }[];
  utilization_mix: { provider_type: string; visits: number; spend: number }[];
  by_drug: { category: string; claims: number; spend: number }[];
  rx_spend: number;
  glp1_spend: number;
  glp1_members: number;
  specialty_spend: number;
  top_drugs: { drug: string; spend: number; members: number }[];
  preventive_claims: number;
  preventive_rate: number;
}

export interface ClaimsAi {
  top_findings?: { title: string; detail: string; financial_impact?: string }[];
  risk_areas?: { area: string; detail: string }[];
  program_recommendations?: {
    program: string;
    rationale: string;
    est_impact?: string;
  }[];
  glp1_analysis?: { summary: string; recommendation: string } | null;
  renewal_impact?: string;
  benchmarks?: { pmpm_vs_national?: string; notes?: string };
  error?: string;
}

export interface ClaimsAnalysisResult {
  stats: ClaimsStats;
  ai: ClaimsAi;
}

export interface ClaimsReportDetail extends ClaimsReport {
  analysis_result: ClaimsAnalysisResult | null;
}

export interface ClaimsUploadResponse {
  claims: ClaimsReport;
  column_map: Record<string, string>;
  unmapped_headers: string[];
  needs_mapping?: string[];
}

export async function listClaims(): Promise<ClaimsReport[]> {
  const res = await client.get<ClaimsReport[]>("/benefits/claims");
  return res.data;
}

export async function getClaims(id: string): Promise<ClaimsReportDetail> {
  const res = await client.get<ClaimsReportDetail>(`/benefits/claims/${id}`);
  return res.data;
}

export async function uploadClaims(opts: {
  file: File;
  carrier_name: string;
  report_period_start: string;
  report_period_end: string;
  data_type: string;
}): Promise<ClaimsUploadResponse> {
  const fd = new FormData();
  fd.append("file", opts.file);
  fd.append("carrier_name", opts.carrier_name);
  fd.append("report_period_start", opts.report_period_start);
  fd.append("report_period_end", opts.report_period_end);
  fd.append("data_type", opts.data_type);
  const res = await client.post<ClaimsUploadResponse>(
    "/benefits/claims/upload",
    fd,
    { headers: { "Content-Type": "multipart/form-data" } },
  );
  return res.data;
}

export async function analyzeClaims(
  id: string,
): Promise<{
  analysis_result: ClaimsAnalysisResult;
  status: string;
  error?: string;
}> {
  const res = await client.post(`/benefits/claims/${id}/analyze`);
  return res.data;
}

export async function deleteClaims(id: string): Promise<void> {
  await client.delete(`/benefits/claims/${id}`);
}

export interface ClaimsComparison {
  earlier: { id: string; label: string | null; period: string | null; total_spend: number; pmpm: number };
  later: { id: string; label: string | null; period: string | null; total_spend: number; pmpm: number };
  total_spend_delta: number;
  pmpm_delta: number;
  glp1_delta: number;
  category_deltas: {
    category: string;
    before: number;
    after: number;
    delta: number;
    pct_change: number | null;
    direction: "up" | "down" | "flat";
  }[];
  new_categories: string[];
}

export async function compareClaims(
  a: string,
  b: string,
): Promise<ClaimsComparison> {
  const res = await client.get<ClaimsComparison>("/benefits/claims/compare", {
    params: { a, b },
  });
  return res.data;
}

export async function createShareLink(
  id: string,
): Promise<{ token: string; url: string; expires_at: string }> {
  const res = await client.post(`/benefits/claims/${id}/share`);
  return res.data;
}

export async function revokeShareLink(token: string): Promise<void> {
  await client.post(`/benefits/claims/share/${token}/revoke`);
}

/** Public read-only fetch for the broker share page (no auth). */
export async function viewSharedClaims(token: string): Promise<{
  carrier_name: string | null;
  data_type: string;
  report_period_start: string | null;
  report_period_end: string | null;
  stats: ClaimsStats | null;
  ai: ClaimsAi | null;
  read_only: boolean;
}> {
  const res = await client.get(`/benefits/claims/shared/${token}`);
  return res.data;
}

/** Download the broker-ready PDF analysis. */
export async function downloadClaimsPdf(id: string): Promise<void> {
  const res = await client.get<Blob>(`/benefits/claims/${id}/report.pdf`, {
    responseType: "blob",
  });
  const disp = (res.headers?.["content-disposition"] as string) ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disp);
  const filename = match?.[1] ?? "claims-analysis.pdf";
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
